import type { RouteObject } from 'react-router';

/**
 * Flat list of every parameterised route pattern the app exposes
 * (e.g. "/people/students/:id/fees"). Populated once the dynamic
 * route tree finishes building so consumers can read it
 * synchronously via {@link getAppRoutes}.
 */
let registeredRoutes: string[] = [];

export function registerAppRoutes(routes: RouteObject[]): void {
  registeredRoutes = flattenRoutes(routes);
}

export function getAppRoutes(): string[] {
  return registeredRoutes;
}

/**
 * Extracts the top-level path segment from a route pattern so UIs can
 * group routes by feature area (e.g. "/people/students/:id/fees" →
 * "people"). Returns an empty string for the root path so the grouping
 * falls under an "other" bucket rather than crashing.
 */
export function getTopLevelRouteSegment(path: string): string {
  const match = path.match(/^\/([^/]+)/);
  return match ? match[1] : '';
}

function flattenRoutes(routes: RouteObject[], prefix = ''): string[] {
  const collected = new Set<string>();

  for (const route of routes) {
    const fullPath = joinPaths(prefix, route.path);

    if (isLeafPattern(fullPath)) {
      collected.add(fullPath);
    }

    const children = (route as { children?: RouteObject[] }).children;
    if (children && children.length > 0) {
      for (const inherited of flattenRoutes(children, fullPath)) {
        collected.add(inherited);
      }
    }
  }

  return Array.from(collected).sort();
}

function joinPaths(prefix: string, path?: string): string {
  if (!path) return prefix;
  if (path.startsWith('/')) return path;
  if (!prefix) return `/${path}`;
  return `${prefix.replace(/\/$/, '')}/${path}`;
}

function isLeafPattern(path: string): boolean {
  if (!path || path === '/') return false;
  // Skip wildcards and layout-only paths.
  if (path.includes('*')) return false;
  return true;
}
